import { FC } from "react"
import { Alert, Box, Button, LinearProgress, Skeleton } from "@mui/material"
import { getApiErrorMessage } from "@/core/utils"
import { BrushPalette, PeriodToolbar, ScheduleGrid } from "../components"
import {
  useBrush,
  usePaintCommit,
  useScheduleData,
  useSchedulePeriod,
  useScheduleWriteAccess,
} from "../hooks"
import { StaffSchedulePage } from "./staffSchedulePage"

/**
 * График присутствия: грид «сотрудники × даты».
 *
 * usePaintCommit инстанцируется ЗДЕСЬ и только один раз — результат раздаётся гриду
 * (pendingKeys, commit) и тулбару (isBusy). Второй инстанс создал бы вторую очередь
 * коммитов и второе множество ячеек «в полёте».
 */
export const SchedulePage: FC = () => {
  const { period, setWeeks, shift, goCurrentWeek, setStart } =
    useSchedulePeriod()
  const data = useScheduleData(period)
  const canWrite = useScheduleWriteAccess()
  const { brush, toggleShift, toggleEraser, clearBrush } = useBrush(data.legend)
  const paint = usePaintCommit(period, data.cells, brush, data.refetchSchedules)

  return (
    <StaffSchedulePage title="График присутствия">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: "calc(100vh - 220px)",
        }}
      >
        <PeriodToolbar
          period={period}
          isBusy={paint.isBusy}
          onSetWeeks={setWeeks}
          onShift={shift}
          onCurrentWeek={goCurrentWeek}
          onSetStart={setStart}
          onRefresh={data.refetchSchedules}
        />
        {/* Место под индикатор зарезервировано всегда — иначе грид дёргается на каждый перезапрос */}
        <Box sx={{ height: 4 }}>
          {data.isFetching && !data.isLoading && <LinearProgress />}
        </Box>
        <BrushPalette
          legend={data.legend}
          countsByShiftType={data.countsByShiftType}
          brush={brush}
          canWrite={canWrite}
          onToggleShift={toggleShift}
          onToggleEraser={toggleEraser}
        />
        {data.error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={data.refetchAll}>
                Повторить
              </Button>
            }
          >
            {getApiErrorMessage(data.error)}
          </Alert>
        ) : data.isLoading ? (
          <Skeleton variant="rectangular" sx={{ flex: 1 }} />
        ) : data.staff.length === 0 ? (
          <Alert severity="info">Нет активных сотрудников</Alert>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ScheduleGrid
              staff={data.staff}
              days={period.days}
              cells={data.cells}
              legend={data.legend}
              pendingKeys={paint.pendingKeys}
              totalsByStaff={data.totalsByStaff}
              totalsByDay={data.totalsByDay}
              grandTotal={data.grandTotal}
              brush={brush}
              canWrite={canWrite}
              onStrokeCommit={paint.commit}
              onClearBrush={clearBrush}
            />
          </Box>
        )}
      </Box>
    </StaffSchedulePage>
  )
}
